import React, { useState, useEffect } from 'react';
import {
  Users,
  Building2,
  Shield,
  BarChart3,
  AlertTriangle,
  CheckCircle,
  Clock,
  Server,
  Eye,
  FileText,
  Lock,
  Mail,
  BookOpen,
  Upload,
  Sparkles,
} from 'lucide-react';
import { notify } from '@core/notifications/notify';
import { ManagementTabProps } from './types';

const ManagementTab: React.FC<ManagementTabProps> = ({
  companyStats,
  frameworkUsage,
  navigate,
  demoModalOpen,
  onCloseDemoModal,
}) => {
  // Demo framework generator local state
  const [demoDescription, setDemoDescription] = useState('');
  const [demoGenerating, setDemoGenerating] = useState(false);
  const [demoResult, setDemoResult] = useState<any>(null);
  const [demoError, setDemoError] = useState<string | null>(null);
  const [demoFrameworks, setDemoFrameworks] = useState<any[]>([]);
  const [demoResetting, setDemoResetting] = useState(false);

  const fetchDemoFrameworks = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/admin/demo/frameworks', { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setDemoFrameworks(data.data || []);
    } catch { /* ignore */ }
  };

  const generateDemoFramework = async () => {
    if (!demoDescription.trim()) return;
    setDemoGenerating(true);
    setDemoError(null);
    setDemoResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/admin/demo/generate-framework', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-csrf-token': document.cookie.split('csrf_token=')[1]?.split(';')[0] || ''
        },
        body: JSON.stringify({ description: demoDescription })
      });
      const data = await res.json();
      if (data.success) {
        setDemoResult(data.data);
        setDemoDescription('');
        fetchDemoFrameworks();
        notify.success(data.message);
      } else {
        setDemoError(data.message);
      }
    } catch (err: any) {
      setDemoError(err.message || 'Generation failed');
    } finally {
      setDemoGenerating(false);
    }
  };

  const resetDemoData = async () => {
    setDemoResetting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/admin/demo/reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-csrf-token': document.cookie.split('csrf_token=')[1]?.split(';')[0] || ''
        }
      });
      const data = await res.json();
      if (data.success) {
        setDemoFrameworks([]);
        setDemoResult(null);
        notify.success(data.message);
      }
    } catch {
      notify.error('Failed to reset demo data');
    } finally {
      setDemoResetting(false);
    }
  };

  // Fetch demo frameworks when modal opens
  useEffect(() => {
    if (demoModalOpen) {
      fetchDemoFrameworks();
    }
  }, [demoModalOpen]);

  return (
    <div className="space-y-6">
      {/* Company & Framework Management */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Company Status Overview */}
        <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <h3 className="text-sm font-semibold text-slate-800 mb-4">Company Status Overview</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
              <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center mb-3">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="text-2xl font-bold text-emerald-600">{companyStats?.active || 0}</div>
              <div className="text-sm text-emerald-700">Active Companies</div>
              <div className="text-xs text-emerald-600 mt-1">Fully operational</div>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center mb-3">
                <Clock className="w-5 h-5 text-slate-600" />
              </div>
              <div className="text-2xl font-bold text-slate-600">{companyStats?.inactive || 0}</div>
              <div className="text-sm text-slate-700">Inactive Companies</div>
              <div className="text-xs text-slate-600 mt-1">Pending activation</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center mb-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div className="text-2xl font-bold text-red-600">{companyStats?.expiring || 0}</div>
              <div className="text-sm text-red-700">Expiring Soon</div>
              <div className="text-xs text-red-600 mt-1">Requires attention</div>
            </div>
          </div>
        </div>

        {/* Framework Usage Analytics */}
        <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <h3 className="text-sm font-semibold text-slate-800 mb-4">Framework Usage Analytics</h3>
          <div className="space-y-6">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Most Used Framework</span>
                <span className="text-sm text-slate-500">ISO 27001</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <div className="bg-blue-600 h-2 rounded-full" style={{ width: '85%' }}></div>
              </div>
              <div className="text-xs text-slate-500 mt-1">85% of companies use this framework</div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Average Compliance Score</span>
                <span className="text-sm text-slate-500">92%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <div className="bg-emerald-600 h-2 rounded-full" style={{ width: '92%' }}></div>
              </div>
              <div className="text-xs text-slate-500 mt-1">Above target threshold</div>
            </div>
          </div>
        </div>
      </div>

      {/* Enterprise Control Panel */}
      <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
        <h3 className="text-sm font-semibold text-slate-800 mb-4">Enterprise Control Panel</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-3">
            <h4 className="font-medium text-slate-700 text-sm uppercase tracking-wide">User & Access Management</h4>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/admin/users')}
                className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <Users className="text-green-600" size={20} />
                <div className="text-left">
                  <div className="text-sm font-medium text-slate-900">User Management</div>
                  <div className="text-xs text-slate-500">Manage system users</div>
                </div>
              </button>
              <button
                onClick={() => navigate('/admin/companies')}
                className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <Building2 className="text-blue-600" size={20} />
                <div className="text-left">
                  <div className="text-sm font-medium text-slate-900">Company Management</div>
                  <div className="text-xs text-slate-500">Manage organizations</div>
                </div>
              </button>
              <button
                onClick={() => navigate('/admin/users')}
                className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <Shield className="text-purple-600" size={20} />
                <div className="text-left">
                  <div className="text-sm font-medium text-slate-900">User Management</div>
                  <div className="text-xs text-slate-500">Manage users & permissions</div>
                </div>
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium text-slate-700 text-sm uppercase tracking-wide">Compliance & Frameworks</h4>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/admin/frameworks')}
                className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <FileText className="text-orange-600" size={20} />
                <div className="text-left">
                  <div className="text-sm font-medium text-slate-900">Compliance Records</div>
                  <div className="text-xs text-slate-500">View submissions</div>
                </div>
              </button>
              <button
                onClick={() => navigate('/compliance/definition/assignments')}
                className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <CheckCircle className="text-emerald-600" size={20} />
                <div className="text-left">
                  <div className="text-sm font-medium text-slate-900">Framework Assignments</div>
                  <div className="text-xs text-slate-500">Manage assignments</div>
                </div>
              </button>
              <button
                onClick={() => navigate('/compliance/definition/frameworks')}
                className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <BarChart3 className="text-sky-700" size={20} />
                <div className="text-left">
                  <div className="text-sm font-medium text-slate-900">Compliance Records</div>
                  <div className="text-xs text-slate-500">View submissions</div>
                </div>
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium text-slate-700 text-sm uppercase tracking-wide">System & Security</h4>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/admin/audit-logs')}
                className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <Eye className="text-purple-600" size={20} />
                <div className="text-left">
                  <div className="text-sm font-medium text-slate-900">Audit Logs</div>
                  <div className="text-xs text-slate-500">System activity</div>
                </div>
              </button>
              <button
                onClick={() => navigate('/admin/access-control')}
                className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <Lock className="text-red-600" size={20} />
                <div className="text-left">
                  <div className="text-sm font-medium text-slate-900">Access Control</div>
                  <div className="text-xs text-slate-500">Security settings</div>
                </div>
              </button>
              <button
                onClick={() => navigate('/admin/system-logs')}
                className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <Server className="text-slate-600" size={20} />
                <div className="text-left">
                  <div className="text-sm font-medium text-slate-900">System Logs</div>
                  <div className="text-xs text-slate-500">Server monitoring</div>
                </div>
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium text-slate-700 text-sm uppercase tracking-wide">Communication & Content</h4>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/admin/email-center')}
                className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <Mail className="text-blue-600" size={20} />
                <div className="text-left">
                  <div className="text-sm font-medium text-slate-900">Email Center</div>
                  <div className="text-xs text-slate-500">Email management</div>
                </div>
              </button>
              <button
                onClick={() => navigate('/compliance/definition/guidance')}
                className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <BookOpen className="text-green-600" size={20} />
                <div className="text-left">
                  <div className="text-sm font-medium text-slate-900">Guidance & Process Maps</div>
                  <div className="text-xs text-slate-500">Compliance guidance</div>
                </div>
              </button>
              <button
                onClick={() => navigate('/compliance/definition/parser')}
                className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <Upload className="text-orange-600" size={20} />
                <div className="text-left">
                  <div className="text-sm font-medium text-slate-900">Framework Parser</div>
                  <div className="text-xs text-slate-500">Parse standards</div>
                </div>
              </button>
              <button
                onClick={() => navigate('/compliance/definition/templates')}
                className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <FileText className="text-blue-600" size={20} />
                <div className="text-left">
                  <div className="text-sm font-medium text-slate-900">Templates</div>
                  <div className="text-xs text-slate-500">Manage templates</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Framework Modal */}
      {demoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => !demoGenerating && onCloseDemoModal()}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sky-500/20 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-sky-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Demo Framework Generator</h2>
                  <p className="text-xs text-slate-400">AI generates a full framework for client demos</p>
                </div>
              </div>
              <button onClick={() => !demoGenerating && onCloseDemoModal()} className="text-slate-400 hover:text-white transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Active demo frameworks */}
              {demoFrameworks.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-300 mb-2">Active Demo Frameworks</h3>
                  <div className="space-y-2">
                    {demoFrameworks.map((fw: any) => (
                      <div key={fw.assignment_id} className="flex items-center justify-between bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-3">
                        <div>
                          <div className="text-sm font-medium text-white">{fw.framework_name}</div>
                          <div className="text-xs text-slate-400">v{fw.version} &middot; {fw.page_count} pages &middot; {fw.status}</div>
                        </div>
                        <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded-full">Active</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={resetDemoData}
                    disabled={demoResetting}
                    className="mt-3 w-full py-2 text-xs font-semibold text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg hover:bg-red-500/20 transition-colors disabled:opacity-50"
                  >
                    {demoResetting ? 'Resetting...' : `Reset All Demo Data (${demoFrameworks.length} framework${demoFrameworks.length > 1 ? 's' : ''}, pages, evidence)`}
                  </button>
                </div>
              )}

              {/* Generate new */}
              <div>
                <h3 className="text-sm font-semibold text-slate-300 mb-2">
                  {demoFrameworks.length > 0 ? 'Generate Another Framework' : 'Describe the Framework'}
                </h3>
                <p className="text-xs text-slate-400 mb-3">
                  Describe the type of compliance framework your client needs. The AI will create the full framework, publish it, and assign it to the demo company with compliance pages ready.
                </p>
                <textarea
                  value={demoDescription}
                  onChange={e => setDemoDescription(e.target.value)}
                  placeholder="e.g. ISO 27001 information security framework for a fintech company processing payment data, or a food safety HACCP framework for a restaurant chain..."
                  className="w-full h-28 bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 resize-none focus:outline-none focus:border-sky-500 transition-colors"
                  disabled={demoGenerating}
                />
              </div>

              {/* Error */}
              {demoError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-300">
                  {demoError}
                </div>
              )}

              {/* Result */}
              {demoResult && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span className="text-sm font-semibold text-emerald-200">Framework Ready!</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="bg-slate-800/50 rounded-lg px-3 py-2">
                      <div className="text-slate-400">Framework</div>
                      <div className="text-white font-medium">{demoResult.framework_name}</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg px-3 py-2">
                      <div className="text-slate-400">Version</div>
                      <div className="text-white font-medium">{demoResult.version}</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg px-3 py-2">
                      <div className="text-slate-400">Nodes Created</div>
                      <div className="text-white font-medium">{demoResult.nodes_created}</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg px-3 py-2">
                      <div className="text-slate-400">Compliance Pages</div>
                      <div className="text-white font-medium text-emerald-300">{demoResult.pages_generated}</div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mt-3">
                    The demo Company Admin can now log in and assign pages to compliance users.
                  </p>
                </div>
              )}

              {/* Generate button */}
              <button
                onClick={generateDemoFramework}
                disabled={demoGenerating || !demoDescription.trim()}
                className="w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed bg-sky-600 text-white shadow-lg shadow-sky-500/25"
              >
                {demoGenerating ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 50" /></svg>
                    Generating framework... (30-60 seconds)
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Generate & Assign to Demo Company
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagementTab;
